// NOT read as a module-level constant: this frontend is built once and shared
// across every tenant (see next.config.ts's "single shared image" note), so a
// bare `process.env.NEXT_PUBLIC_*` read here gets baked into the client bundle
// at `next build` time and is empty for every tenant's real runtime value.
// Server-side (SSR/RSC) reads of process.env still see the live per-request
// value, so only the client path needs the window.__* escape hatch that
// app/layout.tsx injects from a live server-rendered <script>.
function getMediaCdnUrl(): string {
    if (typeof window !== 'undefined') {
        const injected = (window as unknown as { __MEDIA_CDN_URL__?: string }).__MEDIA_CDN_URL__;
        if (typeof injected === 'string') return injected.replace(/\/$/, '');
    }
    return process.env.NEXT_PUBLIC_MEDIA_URL ? process.env.NEXT_PUBLIC_MEDIA_URL.replace(/\/$/, '') : '';
}

function getMediaPrefix(): string {
    if (typeof window !== 'undefined') {
        const injected = (window as unknown as { __MEDIA_PREFIX__?: string }).__MEDIA_PREFIX__;
        if (typeof injected === 'string') return injected.replace(/^\/|\/$/g, '');
    }
    return process.env.NEXT_PUBLIC_MEDIA_PREFIX ? process.env.NEXT_PUBLIC_MEDIA_PREFIX.replace(/^\/|\/$/g, '') : '';
}

/**
 * Theme JSON (shopstag_themes) ships content placeholders like "{{about_image_url}}"
 * that are meant to be replaced with a real media path before import. If one slips
 * through unresolved, it must NOT be turned into a URL — next.config.ts's remotePatterns
 * only allow /static/** paths, so "https://<host>/{{about_image_url}}" gets rejected by
 * the image optimizer with a 400 ("url parameter is not allowed"), breaking the block.
 */
export function isUnresolvedPlaceholder(value: string | null | undefined): boolean {
    return typeof value === 'string' && /\{\{.*\}\}/.test(value);
}

function formatCdnUrl(cleanPath: string): string {
    const mediaCdnUrl = getMediaCdnUrl();
    if (!mediaCdnUrl) return `/${cleanPath}`;
    const mediaPrefix = getMediaPrefix();
    let finalPath = cleanPath;
    if (mediaPrefix && !cleanPath.startsWith(`${mediaPrefix}/`)) {
        finalPath = `${mediaPrefix}/${cleanPath}`;
    }
    return `${mediaCdnUrl}/${finalPath}`;
}

/**
 * Resolves a media path to a root-relative or CDN URL.
 */
export function getAbsoluteMediaUrl(path: string | null | undefined): string {
    if (!path || isUnresolvedPlaceholder(path)) return '';

    // If it's a data URI or blob URL, return as-is
    if (path.startsWith('data:') || path.startsWith('blob:')) {
        return path;
    }

    // If it's already an absolute URL (e.g. https://cdn.shopstag.pl/...), return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
        try {
            const urlObj = new URL(path);
            if (urlObj.pathname.startsWith('/static/') && !getMediaCdnUrl()) {
                return urlObj.pathname;
            }
        } catch {
            // Ignore parsing errors and fall back
        }
        return path;
    }
    
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return formatCdnUrl(cleanPath);
}

/**
 * next.config.ts's images.remotePatterns only allows pathnames matching
 * /static/** or /<tenant>/static/** (see the comment there — a hostname-only
 * wildcard would be SSRF-exploitable, so the path is restricted instead).
 * Anything else — a data: URI, or a fully external URL (Vercel, another CDN,
 * a merchant's old storefront) — would 400 out of next/image's optimizer.
 * Callers should render a plain <img> for those instead of next/image.
 */
export function canOptimizeImage(url: string | null | undefined): boolean {
    if (!url || isUnresolvedPlaceholder(url)) return false;
    if (url.startsWith('data:') || url.startsWith('blob:')) return false;
    const staticPathRe = /^\/(?:[^/]+\/)?static\//;
    if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
            return staticPathRe.test(new URL(url).pathname);
        } catch {
            return false;
        }
    }
    return staticPathRe.test(url.startsWith('/') ? url : `/${url}`);
}

/**
 * Makes a media/static URL absolute so Next.js's built-in image optimizer treats
 * it as remote.
 */
export function toOptimizableSrc(url: string | null | undefined): string {
    if (!url || isUnresolvedPlaceholder(url)) return '';
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const path = url.startsWith('/') ? url.substring(1) : url;
    if (getMediaCdnUrl()) {
        return formatCdnUrl(path);
    }
    const relativePath = url.startsWith('/') ? url : `/${url}`;
    if (typeof window !== 'undefined') {
        return `${window.location.origin}${relativePath}`;
    }
    const origin = process.env.FRONTEND_URL || process.env.NEXTAUTH_URL;
    return origin ? `${origin}${relativePath}` : relativePath;
}

/**
 * Resolves a media path to a browser-accessible URL using CDN or public API URL.
 */
export function getPublicMediaUrl(path: string | null | undefined): string {
    if (!path || isUnresolvedPlaceholder(path)) return '';
    if (path.startsWith('data:') || path.startsWith('blob:')) return path;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;

    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    if (getMediaCdnUrl()) {
        return formatCdnUrl(cleanPath);
    }

    // Static media files must use relative URL when no CDN URL is configured
    if (cleanPath.startsWith('static/')) {
        return `/${cleanPath}`;
    }


    const publicApiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (publicApiUrl) {
        const baseUrl = publicApiUrl.replace(/\/api$/, '').replace(/\/$/, '');
        return `${baseUrl}/${cleanPath}`;
    }
    return `/${cleanPath}`;
}

